import React from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ClipboardDocumentListIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";

import AudioRecorder from "../../utilities/AudioRecorder";
import Transcribe from "./Transcribe";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

// util (kept here in case you later want to format display dates)
const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd for <input type="date">
  } catch {
    return "";
  }
};

const IconButton = ({ label, title, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative group rounded-xl px-3 py-2 text-white bg-sky-800 hover:bg-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 ${className}`}
    aria-label={label}
    title={title || ""}
  >
    {children}
    <span
      role="tooltip"
      className="
        pointer-events-none
        absolute -top-10 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-slate-700 p-2 text-sm text-white shadow
        opacity-0 translate-y-1
        transition-opacity transition-transform duration-150
        group-hover:opacity-100 group-hover:translate-y-0
        group-focus-visible:opacity-100 group-focus-visible:translate-y-0
      "
    >
      {label}
    </span>
  </button>
);

class HealthAssessment extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,

      // AI tools / Smart Paste state
      smartPasteOpen: false,
      smartPasteText: "",
      smartPasteError: "",
      smartPasteBusy: false,
      creditsBalance: null,
      // tab view
      activeTab: "Form"
    };
  }

  // ------ edit + propagate to parent ------

  toggleEdit = () => {
    this.setState((prev) => {
      const wasEditing = prev.isEditing;
      const nextVal = !wasEditing;

      // when turning editing OFF (Save), ask parent to persist
      if (wasEditing && typeof this.props.onSaveDraft === "function") {
        this.props.onSaveDraft();
      }

      return { isEditing: nextVal };
    });
  };

  handleFieldChange = (key, value) => {
    const current = this.props.value || {};
    const health = current.health || {};

    const updatedHealth = {
      ...health,
      [key]: value,
    };

    const nextValue = {
      ...current,
      health: updatedHealth,
    };

    if (typeof this.props.onChange === "function") {
      this.props.onChange(nextValue);
    }
  };

  // --- change tab ---
  handleTabChange = (tab) => {
    this.setState({ activeTab: tab });
  };

  // ---- tools ----
  openConsultationNote = () => {
    toast.info("Consultation note not implemented yet");
  };

  openSmartPaste = () => {
    this.setState({ smartPasteOpen: true, smartPasteText: "", smartPasteError: "" });
  };

  closeSmartPaste = () => {
    if (this.state.smartPasteBusy) return;
    this.setState({ smartPasteOpen: false, smartPasteText: "", smartPasteError: "" });
  };

  // called by AudioRecorder
  handleTranscription = (data) => {
    const transcription = data.transcription || "";
    const creditUsed = data.output?.amount;
    const newBalance = data.output?.newBalance;
    const reason = data.output?.reason;

    if (creditUsed && reason) {
      toast.info(`${creditUsed} credits was used to ${reason}`, {
        autoClose: 1500,
      });
    }

    this.setState(
      {
        creditsBalance: newBalance ?? this.state.creditsBalance,
        smartPasteText: transcription,
      },
      () => {
        this.toastId = toast.loading("Filling up health assessment...");
        this.handleTextProcessing(this.toastId);
      }
    );
  };

  handleTextProcessing = async (toastId) => {
    const { smartPasteText } = this.state;
    if (!smartPasteText.trim()) {
      this.setState({ smartPasteError: "Please paste or type something first." });
      return;
    }

    this.setState({ smartPasteBusy: true, smartPasteError: "" });

    const currentData = this.props.value; 

    try {
      const res = await axios.post(
        `${EndPoint}/careplan/healthassessment`,
        {
          transcription: smartPasteText,
          initData: currentData,
        },
        { withCredentials: true }
      );

      const payload = res?.data || {};
      const ai = JSON.parse(payload?.respond || "{}");

      const current = this.props.value || {};
      const prevHealth = current.health || {};

      const updatedHealth = {
        ...prevHealth,
        // Core HA fields from AI
        substance: ai.substance,
        medicationArrangement: ai.medicationArrangement,
        vision: ai.vision,
        hearing: ai.hearing,
        fallAndHomeSafety: ai.fallAndHomeSafety,
        skin: ai.skin,
        feet: ai.feet,
        nutrition: ai.nutrition,
        oralHealth: ai.oralHealth,
        cognition: ai.cognition,
        mentalHealth: ai.mentalHealth,
        urineContinence: ai.urineContinence,
        faecalContinence: ai.faecalContinence,
        mobility: ai.mobility,
        activity: ai.activity,
        social: ai.social,
        housing: ai.housing,
        legal: ai.legal,
        finance: ai.finance,
        driving: ai.driving,
        advancedCareDirective: ai.advancedCareDirective,
        services: ai.services,
        examination: ai.examination,
        surveillance: ai.surveillance,
        // keep date exactly what user already has
        date: prevHealth.date || "",
      };

      const nextValue = {
        ...current,
        health: updatedHealth,
      };

      if (typeof this.props.onChange === "function") {
        this.props.onChange(nextValue);
      }

      // credits (same pattern as MHCP)
      if (payload?.output) {
        const creditUsed = payload.output.amount;
        const newBalance = payload.output.newBalance;
        const reason = payload.output.reason;
        toast.info(`${creditUsed} credits was used to ${reason}`, {
          autoClose: 1500,
        });
        this.props.onCredits(newBalance);
      }

      this.setState({
        smartPasteBusy: false,
        smartPasteOpen: false,
        smartPasteText: "",
      });

      toast.update(toastId, {
        render: "Health assessment updated",
        type: "success",
        isLoading: false,
        autoClose: 1100,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        smartPasteBusy: false,
        smartPasteError:
          err?.response?.data?.error || err?.message || "Request failed",
      });
      toast.update(toastId, {
        render: "Error updating health assessment",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  // -------- Word / Clipboard export --------

  getHealthWordHTML = () => {
    const { health = {} } = this.props.value || {};

    const esc = (x) =>
      String(x ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const br = (x) =>
      esc(x)
        .replace(/\r?\n/g, "<br/>");

    const base =
      'font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif; font-size:8pt; line-height:1.3; color:#111; ' +
      'mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';

    const h2Style = {
      fontSize: "9pt",
      fontWeight: 700,
      margin: "6pt 0",
      padding: 0,
    };

    const h3Style = {
      fontSize: "8pt",
      fontWeight: 600,
      margin: "6pt 0 2pt",
      padding: 0,
    };

    const bodyTextStyle = {
      fontSize: "8pt",
      lineHeight: "1.3",
    };


    return `
      <!doctype html>
      <html>
      <head><meta charset="utf-8"></head>
      <body>
        <font size="1">
          <div style="${base}">
            <h2 style={h2Style} >Health Assessment</h2 style={h2Style} >

            <h3 style={h3Style} >Date</h3>
            <div style = {bodyTextStyle} >${br(health.date || "")}</div>

            <h3 style={h3Style} >Medication arrangement</h3>
            <div style = {bodyTextStyle} >${br(health.medicationArrangement || "")}</div>

            <h3 style={h3Style} >Substance use</h3>
            <div style = {bodyTextStyle} >${br(health.substance || "")}</div>

            <h3 style={h3Style} >Vision</h3>
            <div style = {bodyTextStyle} >${br(health.vision || "")}</div>

            <h3 style={h3Style} >Hearing</h3>
            <div style = {bodyTextStyle} >${br(health.hearing || "")}</div>

            <h3 style={h3Style} >Falls &amp; home safety</h3>
            <div style = {bodyTextStyle} >${br(health.fallAndHomeSafety || "")}</div>

            <h3 style={h3Style} >Skin</h3>
            <div style = {bodyTextStyle} >${br(health.skin || "")}</div>

            <h3 style={h3Style} >Feet</h3>
            <div style = {bodyTextStyle} >${br(health.feet || "")}</div>

            <h3 style={h3Style} >Nutrition</h3>
            <div style = {bodyTextStyle} >${br(health.nutrition || "")}</div>

            <h3 style={h3Style} >Oral health</h3>
            <div style = {bodyTextStyle} >${br(health.oralHealth || "")}</div>

            <h3 style={h3Style} >Cognition</h3>
            <div style = {bodyTextStyle} >${br(health.cognition || "")}</div>

            <h3 style={h3Style} >Mental health</h3>
            <div style = {bodyTextStyle} >${br(health.mentalHealth || "")}</div>

            <h3 style={h3Style} >Urine continence</h3>
            <div style = {bodyTextStyle} >${br(health.urineContinence || "")}</div>

            <h3 style={h3Style} >Faecal continence</h3>
            <div style = {bodyTextStyle} >${br(health.faecalContinence || "")}</div>

            <h3 style={h3Style} >Mobility</h3>
            <div style = {bodyTextStyle} >${br(health.mobility || "")}</div>

            <h3 style={h3Style} >Activity / exercise</h3>
            <div style = {bodyTextStyle} >${br(health.activity || "")}</div>

            <h3 style={h3Style} >Social</h3>
            <div style = {bodyTextStyle} >${br(health.social || "")}</div>

            <h3 style={h3Style} >Housing</h3>
            <div style = {bodyTextStyle} >${br(health.housing || "")}</div>

            <h3 style={h3Style} >Legal</h3>
            <div style = {bodyTextStyle} >${br(health.legal || "")}</div>

            <h3 style={h3Style} >Finance</h3>
            <div style = {bodyTextStyle} >${br(health.finance || "")}</div>

            <h3 style={h3Style} >Driving</h3>
            <div style = {bodyTextStyle} >${br(health.driving || "")}</div>

            <h3 style={h3Style} >Advance care directive</h3>
            <div style = {bodyTextStyle} >${br(health.advancedCareDirective || "")}</div>

            <h3 style={h3Style} >Services / supports</h3>
            <div style = {bodyTextStyle} >${br(health.services || "")}</div>

            <h3 style={h3Style} >Examination summary</h3>
            <div style = {bodyTextStyle} >${br(health.examination || "")}</div>

            <h3 style={h3Style} >Screening / surveillance</h3>
            <div style = {bodyTextStyle} >${br(health.surveillance || "")}</div>
          </div>
        </font>
      </body>
      </html>`;
  };

  copyHealthToClipboard = async () => {
    const htmlDoc = this.getHealthWordHTML();
    const plain = document.getElementById("health-print-root")?.innerText || "";

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlDoc], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        window.alert(
          "Copied Health Assessment with formatting. Paste into Word with Ctrl/Cmd+V."
        );
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plain);
        window.alert(
          "Copied Health Assessment as plain text (browser doesn’t allow HTML clipboard)."
        );
        return;
      }

      const ta = document.createElement("textarea");
      ta.value = plain;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      window.alert("Copied Health Assessment as plain text.");
    } catch (e) {
      console.error(e);
      window.alert("Copy failed (clipboard permissions?).");
    }
  };

  // -------- Render pieces --------

  renderSmartPasteModal() {
    if (!this.state.smartPasteOpen) return null;

    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) this.closeSmartPaste();
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-xl p-4 flex flex-col"
          style={{ width: 800, height: 800 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mb-2">
            <h3 className="text-lg font-semibold">Smart Paste</h3>
            <p className="text-xs text-gray-500">
              Paste unstructured notes here. On submit, we’ll send it to your AI endpoint.
            </p>
          </div>

          <textarea
            className="flex-1 w-full resize-none rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
            placeholder="Paste or type notes…"
            value={this.state.smartPasteText}
            onChange={(e) =>
              this.setState({
                smartPasteText: e.target.value,
                smartPasteError: "",
              })
            }
          />

          {this.state.smartPasteError && (
            <div className="mt-2 text-sm text-red-600">
              {this.state.smartPasteError}
            </div>
          )}

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
              onClick={this.closeSmartPaste}
              disabled={this.state.smartPasteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm text-white ${
                this.state.smartPasteBusy
                  ? "bg-gray-500"
                  : "bg-black hover:bg-black/90"
              }`}
              onClick={() => {
                this.toastId = toast.loading("Processing...");
                this.handleTextProcessing(this.toastId);
              }}
              disabled={this.state.smartPasteBusy}
            >
              {this.state.smartPasteBusy ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderTools() {
    const currentCredits = this.props.currentCredits;
    const isDisabled = currentCredits < 10;

    return (
      <div className="mb-4 flex items-center justify-center space-x-4 pl-80 sm:pl-[300px]">
        <div
          className={`
            pl-4 flex gap-4 items-center justify-center relative
            ${isDisabled ? "opacity-40 pointer-events-none" : ""}
          `}
          aria-disabled={isDisabled}
          title={
            isDisabled
              ? "You need at least 10 credits to use AI tools"
              : ""
          }
        >
          <IconButton
            label="AI Write or Paste"
            onClick={this.openSmartPaste}
          >
            <ClipboardDocumentListIcon className="w-8 h-8" />
          </IconButton>
          <div className='sm:-ml-[700px]'>
          <Transcribe onInputUpdate={this.handleTranscription} />
          </div>
          <div className="absolute left-14 -bottom-2 text-xs text-gray-500">
            AI Tools
          </div>
        </div>
      </div>
    );
  }

  renderHealthPreview() {
    const { health = {} } = this.props.value || {};

    const fields = [
      { label: "Date", key: "date" },
      { label: "Substance use", key: "substance" },
      { label: "Medication arrangement", key: "medicationArrangement" },
      { label: "Vision", key: "vision" },
      { label: "Hearing", key: "hearing" },
      { label: "Falls & home safety", key: "fallAndHomeSafety" },
      { label: "Skin", key: "skin" },
      { label: "Feet", key: "feet" },
      { label: "Nutrition", key: "nutrition" },
      { label: "Oral health", key: "oralHealth" },
      { label: "Cognition", key: "cognition" },
      { label: "Mental health", key: "mentalHealth" },
      { label: "Urine continence", key: "urineContinence" },
      { label: "Faecal continence", key: "faecalContinence" },
      { label: "Mobility", key: "mobility" },
      { label: "Activity / exercise", key: "activity" },
      { label: "Social", key: "social" },
      { label: "Housing", key: "housing" },
      { label: "Legal", key: "legal" },
      { label: "Finance", key: "finance" },
      { label: "Driving", key: "driving" },
      { label: "Advance care directive", key: "advancedCareDirective" },
      { label: "Services / supports", key: "services" },
      { label: "Examination summary", key: "examination" },
      { label: "Screening / surveillance", key: "surveillance" },
    ];


    const subscription = this.props.hasSubscription

    const {hasSubscription} = this.state;
    if (hasSubscription === null) {
      return null;
    }

    return (
      <div
        id="health-print-root"
        className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none mt-4"
      >
     <button
        className={`
          bg-sky-700 text-white px-3 py-2 rounded-md text-md mb-3
          transition
          ${
            subscription === false
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-sky-800"
          }
        `}
        disabled={this.props.hasSubcription === false}
        onClick={
          subscription === false
            ? undefined
            : this.copyHealthToClipboard
        }
      >
        Copy Health Assessment
      </button>


        <div className="prose max-w-none relative text-sm text-black">
          <h2 className="mt-2 text-xl font-bold text-center">
            Health Assessment
          </h2>
             {!subscription && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="px-12 py-20 border-4 border-red-600 text-red-600 text-[10rem] font-extrabold uppercase tracking-[0.2em] opacity-40 rotate-[-20deg]">
                Demo Only
              </div>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {fields.map(({ label, key }) => {
              const value = health[key];
              if (!value || String(value).trim() === "") return null;

              return (
                <div key={key} className="text-sm">
                  <strong>{label}: </strong>
                  <span className="whitespace-pre-wrap">{value}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ---- main form ----
  renderForm() {
    const { isEditing } = this.state;
    const { health = {} } = this.props.value || {};

    const fields = [
      { label: "Substance use", key: "substance" },
      { label: "Medication arrangement", key: "medicationArrangement" },
      { label: "Vision", key: "vision" },
      { label: "Hearing", key: "hearing" },
      { label: "Falls & home safety", key: "fallAndHomeSafety" },
      { label: "Skin", key: "skin" },
      { label: "Feet", key: "feet" },
      { label: "Nutrition", key: "nutrition" },
      { label: "Oral health", key: "oralHealth" },
      { label: "Cognition", key: "cognition" },
      { label: "Mental health", key: "mentalHealth" },
      { label: "Urine continence", key: "urineContinence" },
      { label: "Faecal continence", key: "faecalContinence" },
      { label: "Mobility", key: "mobility" },
      { label: "Activity / exercise", key: "activity" },
      { label: "Social", key: "social" },
      { label: "Housing", key: "housing" },
      { label: "Legal", key: "legal" },
      { label: "Finance", key: "finance" },
      { label: "Driving", key: "driving" },
      { label: "Advance care directive", key: "advancedCareDirective" },
      { label: "Services / supports", key: "services" },
      { label: "Examination summary", key: "examination" },
      { label: "Screening / surveillance", key: "surveillance" },
    ];

    return (
      <div className="col-span-6 p-4 bg-slate-100 rounded shadow-md m-2">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-center">Health Assessment</h2>
          <button
            onClick={this.toggleEdit}
            className={`px-4 py-2 rounded text-white ${
              isEditing
                ? "bg-sky-600 hover:bg-sky-700"
                : "bg-yellow-500 hover:bg-yellow-600"
            }`}
          >
            {isEditing ? "Save" : "Edit"}
          </button>
        </div>

        {/* Date row */}
        <div className="mt-4 mb-4 max-w-xs">
          <h3 className="text-sm font-semibold">Date</h3>
          <input
            type="date"
            className="w-full text-black text-sm border-gray-200 rounded-md"
            value={health.date || ""}
            readOnly={!isEditing}
            disabled={!isEditing}
            onChange={(e) => this.handleFieldChange("date", e.target.value)}
          />
        </div>

        {/* Main form: 2 columns on md+, 1 column on xs */}
        <div className="mt-2 grid grid-cols-2 gap-4 xs:grid-cols-1">
          {fields.map((field) => (
            <div key={field.key}>
              <h3 className="text-sm font-semibold">{field.label}</h3>
              <textarea
                className="w-full h-32 resize-none text-black text-sm border border-gray-200 rounded-md"
                value={health[field.key] || ""}
                readOnly={!isEditing}
                disabled={!isEditing}
                onChange={(e) =>
                  this.handleFieldChange(field.key, e.target.value)
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderActiveTabContent = () => {
    const { activeTab } = this.state;
    if (activeTab === "Form") return this.renderForm();
    return this.renderHealthPreview();
  };

  renderTabs = () => {
    const { activeTab } = this.state;
    return (
      <div className="flex border-b mb-4">
        <button
          type="button"
          onClick={() => this.handleTabChange("Form")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Form"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Form
        </button>
        <button
          type="button"
          onClick={() => this.handleTabChange("Preview")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Preview"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Preview
        </button>
      </div>
    );
  };


  render() {

    return (
      <div
        className="col-span-6 grid-rows-4 xs:grid-cols-1 md:grid-cols-1"
        style={{
          gridTemplateRows:
            "minmax(12px, auto) minmax(12px, auto) minmax(320px, auto)",
        }}
      >
        {/* Title */}
        <div
          id="main"
          className="text-center col-span-6 text-3xl font-bold py-4 text-sky-900"
        >
          Health Assessment
        </div>

        {/* Tools */}
        {this.renderTools()}

        {/* Form */}
        {/* <div className="tabContent col-span-6">{this.renderForm()}</div> */}
        {this.renderTabs()}
        {this.renderActiveTabContent()}
        {this.renderSmartPasteModal()}
 
        <ToastContainer />
      </div>
    );
  }
}

export default HealthAssessment;
